import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState({ google_enabled: false, google_client_id: "", twilio_enabled: false, stripe_connect_real: false });

    const loadConfig = useCallback(async () => {
        try {
            const { data } = await api.get("/config");
            setConfig(data);
        } catch (e) {
            console.error("Failed to load app config:", e);
        }
    }, []);

    const refreshUser = useCallback(async () => {
        // Auth is cookie-based (httpOnly). Ask the server who we are; a 401 simply means logged out.
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (e) {
            if (e?.response?.status !== 401) {
                console.error("Failed to refresh session:", e);
            }
            setUser(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadConfig();
        refreshUser();
    }, [loadConfig, refreshUser]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data.user);
        return data.user;
    };

    const signup = async (payload) => {
        const { data } = await api.post("/auth/signup", payload);
        setUser(data.user);
        return data;
    };

    const googleLogin = async (credential) => {
        const { data } = await api.post("/auth/google", { credential });
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            console.error("Logout request failed:", e);
        }
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, setUser, loading, config, login, signup, googleLogin, logout, refreshUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
